import { useState, useEffect } from "react";

/* ---- Galerie: o singură poză/lucrătură (înainte & după în aceeași imagine) ---- */
const START = 4;
const COUNT = 18;
const GALLERY_IMAGES = Array.from({ length: COUNT }, (_, i) => `/curatenie/${START + i}.jpg`);

export default function Home() {
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  useEffect(() => {
    if (!lightbox.open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox({ open: false, index: 0 });
      if (e.key === "ArrowLeft")
        setLightbox((s) => ({ ...s, index: (s.index - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length }));
      if (e.key === "ArrowRight")
        setLightbox((s) => ({ ...s, index: (s.index + 1) % GALLERY_IMAGES.length }));
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox.open]);

  return (
    <main className="bg-gradient-to-b from-sky-50 via-white to-white text-slate-900">
      {/* Hero */}
      <section id="home" className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10 opacity-15"
          aria-hidden="true"
          style={{
            backgroundImage: "url('/curatenie/1.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-14 md:grid-cols-2 md:items-center md:py-20">
          <div>
            <span className="mb-3 inline-block rounded-full border border-sky-200 bg-white/70 px-3 py-1 text-xs font-medium text-sky-700">
              Curățare profesională · Textile & Piele
            </span>
            <h1 className="text-3xl font-extrabold tracking-tight md:text-5xl">
              Curățăm cu aparatură și soluții profesionale
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-slate-700">
              Igienizare profundă, detergenți ecologici certificați și uscare corectă pentru
              tapițerii textile și din piele — acasă sau la sediul tău.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <a
                href="#servicii"
                className="rounded-xl bg-sky-600 px-5 py-3 text-white shadow-sm transition hover:bg-sky-700"
              >
                Vezi serviciile
              </a>
              <a
                href="tel:+40761686603"
                className="rounded-xl border border-sky-200 bg-white px-5 py-3 font-medium text-sky-700 shadow-sm transition hover:bg-sky-50"
              >
                Sună acum
              </a>
            </div>
          </div>

          {/* Hero gallery */}
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            <img src="/curatenie/1.jpg" alt="Curățare tapițerie cu aparat profesional" className="aspect-[3/4] w-full rounded-2xl object-cover shadow" />
            <img src="/curatenie/24.jpg" alt="Detalii curățare canapea" className="col-span-2 aspect-video w-full rounded-2xl object-cover shadow" />
            <img src="/curatenie/26.jpg" alt="Spălare mochete și covoare" className="col-span-2 aspect-video w-full rounded-2xl object-cover shadow" />
            <img src="/curatenie/27.jpg" alt="Igienizare interior auto cu abur" className="aspect-[3/4] w-full rounded-2xl object-cover shadow" />
          </div>
        </div>
      </section>

      {/* Servicii — grouped cards */}
      <section id="servicii" className="scroll-mt-28">
        <div className="mx-auto max-w-6xl px-4 py-10 md:py-14">
          <div className="rounded-3xl border bg-white p-6 shadow-sm md:p-8">
            <h2 className="text-2xl font-bold md:text-3xl">Oferim următoarele servicii</h2>
            <p className="mt-2 text-slate-700">Curățare profundă, dezinfectare cu abur și detergenți potriviți materialului.</p>

            <div className="mt-6 grid gap-6 md:grid-cols-3">
              <ServiceCard
                href="#covoare"
                title="Curățare Covoare/Mochete"
                icon={<IconCarpetRoll />}
                bullets={[
                  "Aspirare uscată a prafului și a mizeriei grosiere",
                  "Spălare cu detergenți ecologici certificați",
                  "Uscare corectă",
                ]}
              />
              <ServiceCard
                href="#canapele"
                title="Curățare Canapele, Fotolii & Scaune"
                icon={<IconCouchClean />}
                bullets={[
                  "Aspirare în profunzime",
                  "Soluții de curățare adecvate fiecărui material",
                  "Abur pentru dezinfectare + extracție/injecție",
                ]}
              />
              <ServiceCard
                href="#auto"
                title="Curățare Tapițerii Auto"
                icon={<IconCarSeat />}
                bullets={[
                  "Aspirare în detaliu a tuturor suprafețelor",
                  "Spălare textile/piele cu echipamente profesionale",
                  "Ozon pentru ventilație și habitaclu",
                ]}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Detalii servicii — compact sections */}
      <section className="scroll-mt-28 border-t bg-gradient-to-b from-white to-sky-50/40">
        <div className="mx-auto grid max-w-6xl gap-6 px-4 py-10 md:grid-cols-3 md:py-14">
          {/* COVOARE */}
          <div id="covoare" className="rounded-2xl border bg-white p-6 shadow-sm">
            <HeaderWithIcon icon={<IconCarpetRoll />} title="CURĂȚARE COVOARE / MOCHETE" />
            <ul className="mt-5 grid gap-2 text-slate-800">
              <Bullet>aspirare uscată a prafului și a mizeriei grosiere</Bullet>
              <Bullet>spălarea cu detergenți ecologici certificați</Bullet>
              <Bullet>uscare</Bullet>
            </ul>
          </div>

          {/* CANAPELE */}
          <div id="canapele" className="rounded-2xl border bg-white p-6 shadow-sm">
            <HeaderWithIcon icon={<IconCouchClean />} title="CURĂȚARE CANAPELE, FOTOLII, SCAUNE" />
            <ul className="mt-5 grid gap-2 text-slate-800">
              <Bullet>aspirarea în profunzime</Bullet>
              <Bullet>soluții de curățare adecvate fiecărui tip de material</Bullet>
              <Bullet>dezinfectare cu abur și extracție–injecție pentru limpezire</Bullet>
            </ul>
          </div>

          {/* AUTO */}
          <div id="auto" className="rounded-2xl border bg-white p-6 shadow-sm">
            <HeaderWithIcon icon={<IconCarSeat />} title="CURĂȚARE TAPIȚERII AUTO" />
            <ul className="mt-5 grid gap-2 text-slate-800">
              <Bullet>aspirarea în detaliu a tuturor suprafețelor din interiorul mașinii</Bullet>
              <Bullet>
                spălarea textile/piele (scaune, banchete, mochetă, fețe de uși, portbagaj, preșuri) cu detergenți specifici,
                aspirator cu extracție–injecție, abur și tornador
              </Bullet>
              <Bullet>curățarea suprafețelor din plastic + dressing de protecție UV pentru aspect și durabilitate</Bullet>
              <Bullet>generator de ozon pentru sistemul de ventilație și habitaclu</Bullet>
            </ul>
          </div>
        </div>
      </section>

      {/* Galerie — thumbnails + Lightbox */}
      <section id="galerie" className="scroll-mt-28 border-t bg-white">
        <div className="mx-auto max-w-6xl px-4 py-10 md:py-14">
          <h3 className="text-xl font-semibold md:text-2xl">Înainte &amp; După</h3>
          <p className="mt-1 text-slate-700">Click pe orice imagine pentru a o mări.</p>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {GALLERY_IMAGES.map((src, i) => (
              <button
                key={src}
                type="button"
                onClick={() => setLightbox({ open: true, index: i })}
                className="group block overflow-hidden rounded-lg border border-slate-200/70 bg-white"
                aria-label={`Mărește lucrarea ${i + 1}`}
              >
                <img
                  src={src}
                  alt={`Lucrare ${i + 1} — înainte & după`}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                />
              </button>
            ))}
          </div>
        </div>

        <Lightbox
          open={lightbox.open}
          src={GALLERY_IMAGES[lightbox.index]}
          onClose={() => setLightbox({ open: false, index: 0 })}
          onPrev={() => setLightbox((s) => ({ open: true, index: (s.index - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length }))}
          onNext={() => setLightbox((s) => ({ open: true, index: (s.index + 1) % GALLERY_IMAGES.length }))}
        />
      </section>

      {/* Contact (no form) */}
      <section id="contact" className="scroll-mt-28 border-t bg-white">
        <div className="mx-auto max-w-6xl px-4 py-10 md:py-14">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Contact card */}
            <div className="rounded-2xl border bg-white p-6 shadow-sm">
              <h3 className="text-xl font-semibold md:text-2xl">Contact</h3>
              <p className="mt-2 text-slate-700">Programăm rapid și venim cu aparatură profesională.</p>

              <div className="mt-5 grid gap-2">
                <ContactItem href="tel:+40761686603" label="+40 761 686 603" aria="Sună acum">
                  <IconPhone />
                </ContactItem>

                <ContactItem
                  href="https://wa.me/40761686603"
                  label="WhatsApp"
                  aria="Deschide conversația pe WhatsApp"
                  external
                >
                  <IconWhatsAppBlue />
                </ContactItem>

                <ContactItem
                  href="https://www.facebook.com/profile.php?id=100049722475890"
                  label="Facebook — Curățătoria de tapițerii Deniadi"
                  aria="Pagina Facebook Curățătoria de tapițerii Deniadi"
                  external
                >
                  <IconFacebook />
                </ContactItem>

                <ContactItem label="Timișoara">
                  <IconPin />
                </ContactItem>

                <ContactItem label="Curățătoria Deniadi SRL">
                  <IconBuilding />
                </ContactItem>
              </div>
            </div>

            {/* Why us card */}
            <div className="rounded-2xl border bg-sky-50 p-6 shadow-sm">
              <h4 className="text-lg font-semibold text-sky-900">De ce noi?</h4>
              <ul className="mt-3 grid gap-2 text-sm text-sky-900/90">
                <Bullet>Detergenți ecologici certificați</Bullet>
                <Bullet>Abur pentru dezinfectare + extracție/injecție</Bullet>
                <Bullet>Rezultate rapide și curate</Bullet>
              </ul>
              <img
                src="/curatenie/25.jpg"
                alt="Rezultate curățare"
                className="mt-4 aspect-video w-full rounded-xl object-cover shadow"
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

/* -------------------- UI Subcomponents -------------------- */

/* Cleaner, descriptive service icons */
function IconCarpetRoll({ className = "h-6 w-6" }) {
  // rolled carpet with fringe and pattern
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 8h11a3 3 0 1 1 0 6H6c-.6 0-1 .4-1 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5 17v-7a3 3 0 1 1 0 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M6 20v-1M8 20v-1M10 20v-1M12 20v-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M14 11h2M10 11h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );
}

function IconCouchClean({ className = "h-6 w-6" }) {
  // sofa with sparkles to indicate cleaning
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="10" width="16" height="5" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M8 10V8a3 3 0 0 1 3-3h2a3 3 0 0 1 3 3v2" stroke="currentColor" strokeWidth="2" />
      <path d="M4 15v3h16v-3" stroke="currentColor" strokeWidth="2" />
      {/* sparkles */}
      <path d="M6 6h0" stroke="currentColor" strokeWidth="2"/>
      <path d="M6 6l1-1M6 6l-1-1M6 6l1 1M6 6l-1 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M18 6l1-1M18 6l-1-1M18 6l1 1M18 6l-1 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

function IconCarSeat({ className = "h-6 w-6" }) {
  // car seat silhouette with sparkle
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M8 13V7a3 3 0 0 1 3-3h.5a2.5 2.5 0 0 1 2.5 2.5V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M6 16h9a3 3 0 0 1 3 3v1H5v-1a3 3 0 0 1 1-2.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      {/* sparkle */}
      <path d="M17 6l1-1M17 6l-1-1M17 6l1 1M17 6l-1 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

/* Blue WhatsApp icon (circle = currentColor) */
export function IconWhatsAppBlue({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" role="img">
      <circle cx="12" cy="12" r="10" fill="currentColor" />
      <path
        fill="#FFFFFF"
        d="M16.14 13.22c-.21-.1-1.24-.61-1.43-.68-.19-.07-.33-.1-.47.1s-.54.68-.66.82c-.12.14-.24.15-.45.05-.21-.1-.9-.33-1.72-1.05-.64-.55-1.08-1.22-1.21-1.43-.13-.21-.01-.33.09-.43.1-.1.21-.24.31-.36.1-.12.14-.21.21-.35.07-.14.03-.26-.02-.36-.05-.1-.47-1.13-.64-1.55-.17-.41-.34-.35-.47-.36l-.4-.01c-.14 0-.36.05-.55.26-.19.21-.73.71-.73 1.73s.75 2  .86 2.14c.1.14 1.49 2.28 3.62 3.2 2.13.92 2.13.62 2.51.6.38-.02 1.24-.5 1.41-.98.17-.48.17-.89.12-.98-.05-.09-.19-.14-.4-.24Z"
      />
    </svg>
  );
}

function ServiceCard({ title, bullets, icon, href }) {
  return (
    <a href={href} className="group block rounded-2xl border bg-white p-6 shadow-sm transition hover:shadow-md">
      <div className="flex items-center gap-3">
        <div className="rounded-xl border bg-sky-50 p-3 text-sky-700">{icon}</div>
        <h3 className="text-lg font-semibold">{title}</h3>
      </div>
      <ul className="mt-4 grid gap-2 text-sm text-slate-700">
        {bullets.map((b, i) => (
          <li key={i} className="inline-flex items-start gap-2">
            <CheckIcon className="mt-1 h-5 w-5 shrink-0 text-sky-600" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-slate-900">
        Vezi detalii
        <svg className="h-4 w-4 transition group-hover:translate-x-0.5" viewBox="0 0 24 24" fill="none">
          <path d="M5 12h14m0 0l-5-5m5 5l-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
    </a>
  );
}

function HeaderWithIcon({ icon, title }) {
  return (
    <div className="inline-flex items-center gap-3">
      <div className="rounded-xl border bg-sky-50 p-3 text-sky-700">{icon}</div>
      <h3 className="text-lg font-semibold md:text-xl">{title}</h3>
    </div>
  );
}

function Bullet({ children }) {
  return (
    <li className="inline-flex items-start gap-2">
      <CheckIcon className="mt-1 h-5 w-5 shrink-0 text-sky-600" />
      <span>{children}</span>
    </li>
  );
}

function Lightbox({ open, src, onClose, onPrev, onNext }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/80" aria-modal="true" role="dialog" onClick={onClose}>
      <button
        aria-label="Închide"
        className="absolute right-4 top-4 rounded-lg bg-white/10 p-2 text-white backdrop-blur transition hover:bg-white/20"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>

      <div className="flex h-full items-center justify-center px-3" onClick={(e) => e.stopPropagation()}>
        <button
          className="mr-2 hidden rounded-lg bg-white/10 p-3 text-white backdrop-blur transition hover:bg-white/20 md:block"
          aria-label="Anterior"
          onClick={onPrev}
        >
          <svg width="22" height="22" viewBox="0 0 24 24">
            <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
          </svg>
        </button>

        <img src={src} alt="Imagine mărită" className="max-h-[80vh] w-auto max-w-[90vw] rounded-2xl object-contain shadow-2xl" />

        <button
          className="ml-2 hidden rounded-lg bg-white/10 p-3 text-white backdrop-blur transition hover:bg-white/20 md:block"
          aria-label="Următor"
          onClick={onNext}
        >
          <svg width="22" height="22" viewBox="0 0 24 24">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
          </svg>
        </button>
      </div>

      {/* Mobile next/prev */}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-4 md:hidden">
        <button className="rounded-lg bg-white/10 px-4 py-2 text-white backdrop-blur transition hover:bg-white/20" onClick={(e) => { e.stopPropagation(); onPrev(); }}>
          Anterior
        </button>
        <button className="rounded-lg bg-white/10 px-4 py-2 text-white backdrop-blur transition hover:bg-white/20" onClick={(e) => { e.stopPropagation(); onNext(); }}>
          Următor
        </button>
      </div>
    </div>
  );
}

/* -------------------- Inline SVG Icons (no external deps) -------------------- */
function CheckIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPhone({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M21 16.5v2a2.5 2.5 0 0 1-2.7 2.5 16.8 16.8 0 0 1-7.3-3.1 16.6 16.6 0 0 1-5.9-5.9A16.8 16.8 0 0 1 2 4.7 2.5 2.5 0 0 1 4.5 2h2A1.5 1.5 0 0 1 8 3.2c.2 1 .6 2 .9 2.8.3.8.1 1.6-.4 2.1L7 9.7a13.8 13.8 0 0 0 7.3 7.3l1.6-1.5c.5-.5 1.3-.7 2.1-.4.9.3 1.8.7 2.8.9A1.5 1.5 0 0 1 21 16.5Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconFacebook({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M13.5 8.5h1.7V6.2c-.3-.1-1-.2-1.9-.2-2 0-3.3 1.2-3.3 3.4v1.8H8v2.3h2v4.3h2.4v-4.3h2l.4-2.3h-2.4V9.7c0-.7.4-1.2 1.1-1.2Z" fill="currentColor" />
    </svg>
  );
}

function IconPin({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 21s7-4.2 7-10a7 7 0 1 0-14 0c0 5.8 7 10 7 10Z" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="11" r="2.4" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

function IconBuilding({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="6" y="4" width="12" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M9 8h2M13 8h2M9 12h2M13 12h2M9 16h2M13 16h2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M4 20h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

/* ---- Contact helpers ---- */
function ContactItem({ href, label, children, external = false, aria }) {
  const Comp = href ? "a" : "div";
  const props = href ? { href, ...(external ? { target: "_blank", rel: "noreferrer" } : {}), "aria-label": aria || label } : {};
  return (
    <Comp {...props} className="group inline-flex items-center gap-3 rounded-xl p-2 pr-3 transition hover:bg-sky-50">
      <span className="grid h-9 w-9 place-items-center rounded-lg bg-sky-100 text-sky-700">{children}</span>
      <span className="text-sm text-slate-800 group-hover:text-slate-900">{label}</span>
    </Comp>
  );
}
